/**
 * Code Snippet Library
 * Browseable library of reusable code snippets with search, categories, and copy-to-editor
 */

import React, { useState } from 'react';
import {
  Search,
  Copy,
  Check,
  Plus,
  Trash2,
  Code2,
  FileCode,
  X,
} from 'lucide-react';
import type { CodeSnippet } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface SnippetLibraryProps {
  snippets: CodeSnippet[];
  onInsert: (snippet: CodeSnippet) => void;
  onSave?: (snippet: Partial<CodeSnippet>) => Promise<void>;
  onDelete?: (snippetId: string) => Promise<void>;
}

// Built-in snippet presets
const PRESET_SNIPPETS: Partial<CodeSnippet>[] = [
  {
    snippetName: 'Google Analytics 4',
    description: 'Google Analytics tracking code',
    language: 'html',
    code: `<!-- Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXXXXX');
</script>`,
    category: 'analytics',
  },
  {
    snippetName: 'Meta Pixel',
    description: 'Facebook/Meta tracking pixel',
    language: 'html',
    code: `<!-- Meta Pixel Code -->
<script>
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', 'YOUR_PIXEL_ID');
  fbq('track', 'PageView');
</script>`,
    category: 'analytics',
  },
  {
    snippetName: 'Responsive Image',
    description: 'Responsive image with srcset',
    language: 'html',
    code: `<picture>
  <source media="(min-width: 1024px)" srcset="/images/hero-lg.webp" type="image/webp">
  <source media="(min-width: 768px)" srcset="/images/hero-md.webp" type="image/webp">
  <img src="/images/hero-sm.jpg" alt="Description" loading="lazy" class="w-full h-auto">
</picture>`,
    category: 'content',
  },
  {
    snippetName: 'CSS Grid Layout',
    description: 'Responsive grid with auto-fill',
    language: 'css',
    code: `.grid-layout {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 1.5rem;
}`,
    category: 'layout',
  },
  {
    snippetName: 'Smooth Scroll',
    description: 'Smooth scrolling to anchors',
    language: 'javascript',
    code: `document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function(e) {
    e.preventDefault();
    const target = document.querySelector(this.getAttribute('href'));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
});`,
    category: 'interaction',
  },
  {
    snippetName: 'Intersection Observer',
    description: 'Animate elements on scroll into view',
    language: 'javascript',
    code: `const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('animate-in');
      observer.unobserve(entry.target);
    }
  });
}, { threshold: 0.1 });

document.querySelectorAll('.animate-on-scroll').forEach(el => {
  observer.observe(el);
});`,
    category: 'interaction',
  },
];

const LANGUAGE_COLORS: Record<string, string> = {
  html: 'bg-orange-100 text-orange-700',
  css: 'bg-blue-100 text-blue-700',
  javascript: 'bg-yellow-100 text-yellow-700',
};

// ============================================================================
// SNIPPET CARD
// ============================================================================

interface SnippetCardProps {
  snippet: Partial<CodeSnippet>;
  onInsert: () => void;
  onDelete?: () => void;
}

function SnippetCard({ snippet, onInsert, onDelete }: SnippetCardProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(snippet.code || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail
    }
  };

  return (
    <div className="border rounded-lg p-3 hover:border-blue-300 transition-colors group">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="text-sm font-medium text-gray-900">{snippet.snippetName}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{snippet.description}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded font-mono ${
          LANGUAGE_COLORS[snippet.language || ''] || 'bg-gray-100 text-gray-600'
        }`}>
          {snippet.language}
        </span>
      </div>

      {/* Code Preview */}
      <pre className="text-xs font-mono bg-gray-900 text-gray-300 rounded p-2 overflow-hidden max-h-20">
        <code>{(snippet.code || '').slice(0, 200)}{(snippet.code || '').length > 200 ? '...' : ''}</code>
      </pre>

      {/* Actions */}
      <div className="flex items-center justify-between mt-2">
        {snippet.category && (
          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-500">
            {snippet.category}
          </span>
        )}
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={handleCopy}
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            title="Copy code"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={onInsert}
            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Insert
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className="p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SnippetLibrary({
  snippets,
  onInsert,
  onSave,
  onDelete,
}: SnippetLibraryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [languageFilter, setLanguageFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [showPresets, setShowPresets] = useState(true);

  // Combine user snippets with presets
  const allSnippets = showPresets
    ? [...snippets, ...PRESET_SNIPPETS.map((p, i) => ({ ...p, id: `preset_${i}` } as CodeSnippet))]
    : snippets;

  // Get unique categories
  const categories = Array.from(new Set(allSnippets.map(s => s.category).filter(Boolean)));

  // Filter
  const filtered = allSnippets.filter(s => {
    const matchesSearch = !searchQuery ||
      (s.snippetName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.code || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLanguage = !languageFilter || s.language === languageFilter;
    const matchesCategory = !categoryFilter || s.category === categoryFilter;
    return matchesSearch && matchesLanguage && matchesCategory;
  });

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Code2 className="h-5 w-5 text-gray-600" />
          <h3 className="font-semibold">Code Snippets</h3>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b px-4 py-3 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search snippets..."
            className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            className="border rounded px-2 py-1 text-xs"
          >
            <option value="">All Languages</option>
            <option value="html">HTML</option>
            <option value="css">CSS</option>
            <option value="javascript">JavaScript</option>
          </select>
          {categories.length > 0 && (
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border rounded px-2 py-1 text-xs"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
            <input
              type="checkbox"
              checked={showPresets}
              onChange={(e) => setShowPresets(e.target.checked)}
              className="rounded"
            />
            Show presets
          </label>
        </div>
      </div>

      {/* Snippet List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <FileCode className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No snippets found</p>
          </div>
        ) : (
          filtered.map((snippet) => (
            <SnippetCard
              key={snippet.id}
              snippet={snippet}
              onInsert={() => onInsert(snippet as CodeSnippet)}
              onDelete={
                snippet.id?.startsWith('preset_')
                  ? undefined
                  : onDelete
                  ? () => onDelete(snippet.id!)
                  : undefined
              }
            />
          ))
        )}
      </div>
    </div>
  );
}

export default SnippetLibrary;
